import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../../i18n/contentLocales';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import {
  deleteStation,
  listStations,
  permanentlyDeleteStation,
  restoreStation,
} from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { MultilingualString, Station } from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import DataTable, { type DataTableColumn } from '../../components/DataTable';

const StationLibraryList: React.FC = () => {
  const { t, i18n } = useTranslation('content');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<Station | null>(null);
  const [deleting, setDeleting] = useState(false);
  // SOW 8.6.4 (#344) — org_admin archive view + restore / permanent delete
  const isOrgAdmin = user?.role === 'org_admin';
  const [showArchived, setShowArchived] = useState(false);
  const [toDeleteForever, setToDeleteForever] = useState<Station | null>(null);

  const orgSlug = user?.organization_slug;
  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);
  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(activeLang === 'de' ? 'de-DE' : 'en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

  const load = useCallback(async () => {
    if (!orgSlug) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await listStations(orgSlug, { limit: 200, include_deleted: showArchived });
      setStations(resp.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, showArchived]);

  useEffect(() => {
    void load();
  }, [load]);

  const rallyeNames = useCallback(
    (s: Station) => (s.usage?.rallye_titles ?? []).map((rt) => titleOf(rt)).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeLang],
  );

  const handleDelete = async () => {
    if (!orgSlug || !toDelete) return;
    setDeleting(true);
    try {
      await deleteStation(orgSlug, toDelete.id);
      setToDelete(null);
      // Refetch instead of locally filtering: when "Show archived" is on, the
      // just-archived item should stay visible with its archived state
      // (#364), matching what a reload with include_deleted=true would show.
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (s: Station) => {
    if (!orgSlug) return;
    try {
      await restoreStation(orgSlug, s.id);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    }
  };

  const handleDeleteForever = async () => {
    if (!orgSlug || !toDeleteForever) return;
    setDeleting(true);
    try {
      await permanentlyDeleteStation(orgSlug, toDeleteForever.id);
      setStations((prev) => prev.filter((s) => s.id !== toDeleteForever.id));
      setToDeleteForever(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setDeleting(false);
    }
  };

  const columns = useMemo<DataTableColumn<Station>[]>(
    () => [
      {
        id: 'name',
        header: t('station.list.table.name'),
        searchValue: (s) => titleOf(s.title),
        sortValue: (s) => titleOf(s.title),
        render: (s) => (
          <Box component="span" sx={{ fontWeight: 600 }}>
            {titleOf(s.title) || s.id}
            {s.is_deleted && (
              <Chip
                label={t('archive.archivedChip')}
                color="warning"
                size="small"
                variant="outlined"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
        ),
      },
      {
        id: 'rallye',
        header: t('station.list.table.rallye'),
        hideBelow: 'sm',
        render: (s) => {
          const names = rallyeNames(s);
          return (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {names.length > 0 ? names.join(', ') : t('station.list.table.unused')}
            </Box>
          );
        },
      },
      {
        id: 'author',
        header: t('station.list.table.author'),
        hideBelow: 'md',
        sortValue: (s) => s.created_by_display_name ?? '',
        render: (s) => (
          <Box component="span" sx={{ color: 'text.secondary' }}>
            {s.created_by_display_name ?? '—'}
          </Box>
        ),
      },
      {
        id: 'date',
        header: t('station.list.table.date'),
        hideBelow: 'lg',
        sortValue: (s) => s.created_at,
        render: (s) => (
          <Box component="span" sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {fmtDate(s.created_at)}
          </Box>
        ),
      },
      {
        id: 'actions',
        header: t('station.list.table.actions'),
        align: 'right',
        actions: true,
        render: (s) =>
          s.is_deleted ? (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <Tooltip title={t('archive.restore')}>
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={`${t('archive.restore')}: ${titleOf(s.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRestore(s);
                  }}
                >
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('archive.deleteForever')}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`${t('archive.deleteForever')}: ${titleOf(s.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDeleteForever(s);
                  }}
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ) : (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <Tooltip title={t('station.list.table.open')}>
                <IconButton
                  size="small"
                  aria-label={`${t('station.list.table.open')}: ${titleOf(s.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/stations/${s.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('actions.delete', { ns: 'common' })}>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`${t('actions.delete', { ns: 'common' })}: ${titleOf(s.title)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setToDelete(s);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, activeLang, navigate, rallyeNames],
  );

  const deleteRallyeNames = toDelete ? rallyeNames(toDelete) : [];

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: 1,
        }}
      >
        <Typography variant="h2" component="h1">
          {t('station.list.title')}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/stations/new')}>
          {t('station.list.createButton')}
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('station.list.subtitle')}
      </Typography>

      <DataTable<Station>
        rows={stations}
        columns={columns}
        getRowId={(s) => s.id}
        ariaLabel={t('station.list.title')}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage={
          <>
            <Typography variant="body1" color="text.secondary">
              {t('station.list.empty')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('station.list.emptyHint')}
            </Typography>
          </>
        }
        searchPlaceholder={t('station.list.search')}
        defaultSort={{ columnId: 'name', dir: 'asc' }}
        onRowClick={(s) => (s.is_deleted ? undefined : navigate(`/stations/${s.id}`))}
        toolbarActions={
          isOrgAdmin ? (
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                />
              }
              label={t('archive.showArchived')}
            />
          ) : undefined
        }
      />

      <ConfirmDialog
        open={!!toDelete}
        title={t('station.detail.delete')}
        message={
          deleteRallyeNames.length > 0
            ? t('station.detail.deleteInUseConfirm', {
                count: deleteRallyeNames.length,
                rallyes: deleteRallyeNames.join(', '),
              })
            : t('station.detail.deleteConfirm')
        }
        destructive
        loading={deleting}
        confirmLabel={t('actions.delete', { ns: 'common' })}
        onConfirm={handleDelete}
        onCancel={() => setToDelete(null)}
      />

      <ConfirmDialog
        open={!!toDeleteForever}
        title={t('archive.deleteForeverTitle')}
        message={t('archive.deleteForeverConfirm')}
        destructive
        loading={deleting}
        confirmLabel={t('archive.deleteForever')}
        onConfirm={handleDeleteForever}
        onCancel={() => setToDeleteForever(null)}
      />
    </Box>
  );
};

export default StationLibraryList;
